<template>
  <div class="profile-information-page">
    <b-overlay
      :show="loading"
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>

      <template v-if="user">
        <!-- ============ HERO / PROFIL KARTI ============ -->
        <div class="pi-card pi-hero">
          <div
            class="pi-hero-bg"
            aria-hidden="true"
          >
            <div class="pi-hero-glow" />
            <div class="pi-hero-heart heart-big">
              <svg viewBox="-12 -12 124 124">
                <defs>
                  <linearGradient
                    id="piHeroHeartBig"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stop-color="#ff2ea6"
                      stop-opacity="0.95"
                    />
                    <stop
                      offset="55%"
                      stop-color="#ea03b2"
                      stop-opacity="0.6"
                    />
                    <stop
                      offset="100%"
                      stop-color="#7928ca"
                      stop-opacity="0.25"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M50,88 C20,62 5,42 5,26 C5,12 16,3 29,3 C38,3 46,8 50,16 C54,8 62,3 71,3 C84,3 95,12 95,26 C95,42 80,62 50,88 Z"
                  fill="none"
                  stroke="url(#piHeroHeartBig)"
                  stroke-width="6.5"
                />
              </svg>
            </div>
            <div class="pi-hero-heart heart-mid">
              <svg viewBox="-12 -12 124 124">
                <path
                  d="M50,88 C20,62 5,42 5,26 C5,12 16,3 29,3 C38,3 46,8 50,16 C54,8 62,3 71,3 C84,3 95,12 95,26 C95,42 80,62 50,88 Z"
                  fill="none"
                  stroke="url(#piHeroHeartBig)"
                  stroke-width="7"
                />
              </svg>
            </div>
            <div
              v-for="n in 3"
              :key="'small-heart-' + n"
              class="pi-hero-heart heart-small"
              :class="'heart-small-' + n"
            >
              <svg viewBox="-6 -6 36 36">
                <defs>
                  <linearGradient
                    :id="'piHeroHeartSmall' + n"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stop-color="#ff3eab"
                    />
                    <stop
                      offset="100%"
                      stop-color="#ea03b2"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  :fill="'url(#piHeroHeartSmall' + n + ')'"
                />
              </svg>
            </div>
          </div>

          <div class="pi-hero-content">
            <div class="pi-hero-top">
              <div class="pi-avatar-wrap">
                <div class="pi-avatar">
                  <img
                    v-if="user.profile.image"
                    :src="user.profile.image"
                    :alt="user.userName"
                  >
                  <feather-icon
                    v-else
                    icon="UserIcon"
                    size="64"
                  />
                </div>
                <button
                  type="button"
                  class="pi-avatar-edit"
                  :title="$t('Edit your medias')"
                  @click="modalGallery = true"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    size="16"
                  />
                </button>
              </div>

              <div class="pi-identity">
                <h2 class="pi-name">
                  {{ user.userName }}<span
                    v-if="userAge"
                    class="pi-age"
                  >, {{ userAge }}</span>
                </h2>
                <div
                  v-if="user.identifier"
                  class="pi-id"
                >
                  ID: {{ user.identifier }}
                  <button
                    type="button"
                    class="pi-icon-btn"
                    :title="$t('Copy')"
                    @click="copyIdentifier"
                  >
                    <feather-icon
                      icon="CopyIcon"
                      size="15"
                    />
                  </button>
                </div>
                <div
                  v-if="user.profile.country"
                  class="pi-location"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                  />
                  {{ user.profile.country }}
                </div>
              </div>

              <div class="pi-about">
                <div class="pi-about-head">
                  <span>{{ $t('About me') }}</span>
                  <button
                    v-if="!editingAbout"
                    type="button"
                    class="pi-icon-btn"
                    :title="$t('Edit')"
                    @click="startEditAbout"
                  >
                    <feather-icon
                      icon="Edit2Icon"
                      size="16"
                    />
                  </button>
                </div>
                <template v-if="editingAbout">
                  <b-form-textarea
                    v-model="aboutDraft"
                    class="pi-textarea"
                    rows="3"
                    max-rows="5"
                    autofocus
                  />
                  <div class="pi-about-actions">
                    <button
                      type="button"
                      class="pi-btn pi-btn-ghost"
                      @click="cancelEditAbout"
                    >
                      {{ $t('Cancel') }}
                    </button>
                    <button
                      type="button"
                      class="pi-btn pi-btn-primary"
                      @click="saveAbout"
                    >
                      {{ $t('Save') }}
                    </button>
                  </div>
                </template>
                <p v-else>
                  {{ user.profile.description && user.profile.description !== ''
                    ? user.profile.description
                    : $t('You haven\'t added any information about me yet') }}
                </p>
              </div>
            </div>

            <!-- Seviye (rozet) satırı: eski profil sayfasındaki BOHO MEMBER / BRONZE / SILVER / GOLD -->
            <div
              v-if="user.profile.gender === 'male'"
              class="pi-levels"
            >
              <div
                v-for="level in levels"
                :key="level.key"
                class="pi-level"
                :class="{ passive: checkBadge(level.key) }"
              >
                <div class="pi-level-head">
                  <span class="pi-level-icon">
                    <img
                      :src="level.icon"
                      :alt="level.label"
                    >
                  </span>
                  <span class="pi-level-label">{{ level.label }}</span>
                </div>
                <div class="pi-level-bar">
                  <span />
                </div>
              </div>
            </div>

            <div
              v-if="canUploadBonding"
              class="pi-bonding"
            >
              <button
                type="button"
                class="pi-btn pi-btn-primary"
                @click="openBondingFilePicker"
              >
                {{ $t('Start Bonding') }}
              </button>
              <span>{{ $t('(minimum 20 seconds)') }}</span>
            </div>
          </div>
        </div>

        <!-- ============ PROFILINI GUNCELLE ============ -->
        <div class="pi-card pi-form-card">
          <div class="pi-section-head">
            <div class="pi-section-title">
              <span class="pi-section-icon">
                <feather-icon
                  icon="UserIcon"
                  size="20"
                />
              </span>
              <div>
                <h3>{{ $t('Update your profile') }}</h3>
                <p>{{ $t('Update your information so others can get to know you better.') }}</p>
              </div>
            </div>
            <button
              type="button"
              class="pi-btn pi-btn-primary pi-save"
              :disabled="loading"
              @click="update"
            >
              <feather-icon
                icon="CheckIcon"
                size="16"
              />
              {{ $t('Save') }}
            </button>
          </div>

          <validation-observer ref="form">
            <b-form @submit.prevent="update">
              <div class="pi-grid">
                <!-- Ad / Soyad -->
                <div class="pi-field">
                  <div class="pi-split">
                    <div>
                      <label for="firstName">{{ $t('Firstname') }}</label>
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Firstname')"
                        vid="firstName"
                      >
                        <b-form-input
                          id="firstName"
                          v-model="user.firstName"
                          class="pi-input"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>
                    <div>
                      <label for="lastName">{{ $t('Lastname') }}</label>
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Lastname')"
                        vid="lastName"
                      >
                        <b-form-input
                          id="lastName"
                          v-model="user.lastName"
                          class="pi-input"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>
                  </div>
                </div>

                <!-- Kullanici adi (degistirilemez) -->
                <div class="pi-field">
                  <label for="userName">{{ $t('Username') }}</label>
                  <div class="pi-input-with-icon">
                    <b-form-input
                      id="userName"
                      v-model="user.userName"
                      class="pi-input"
                      readonly
                      disabled
                    />
                    <feather-icon
                      v-b-tooltip.hover
                      icon="LockIcon"
                      size="16"
                      :title="$t('Username cannot be changed')"
                    />
                  </div>
                </div>

                <!-- E-posta -->
                <div class="pi-field">
                  <label for="email">{{ $t('Email') }}</label>
                  <div class="pi-input-row">
                    <b-form-input
                      id="email"
                      v-model="user.email"
                      class="pi-input"
                      readonly
                      disabled
                    />
                    <button
                      type="button"
                      class="pi-btn pi-btn-outline"
                      @click="modalUpdateEmail = true"
                    >
                      {{ $t('Change') }}
                    </button>
                  </div>
                </div>

                <!-- Telefon -->
                <div class="pi-field">
                  <label for="phone">{{ $t('Phone number') }}</label>
                  <div class="pi-input-row">
                    <b-form-input
                      id="phone"
                      v-model="user.phoneNumber"
                      class="pi-input"
                      readonly
                      disabled
                    />
                    <button
                      type="button"
                      class="pi-btn pi-btn-outline"
                      @click="modalUpdatePhone = true"
                    >
                      {{ $t('Change') }}
                    </button>
                  </div>
                </div>

                <!-- Cinsiyet (degistirilemez, eskisi gibi) -->
                <div class="pi-field">
                  <label>{{ $t('Gender') }}</label>
                  <div class="pi-gender">
                    <div
                      class="pi-gender-option"
                      :class="{ active: user.profile.gender === 'male' }"
                    >
                      <span class="pi-gender-symbol">&#9794;</span>
                      {{ $t('Male') }}
                    </div>
                    <div
                      class="pi-gender-option"
                      :class="{ active: user.profile.gender === 'female' }"
                    >
                      <span class="pi-gender-symbol">&#9792;</span>
                      {{ $t('Female') }}
                    </div>
                  </div>
                </div>

                <!-- Dogum tarihi -->
                <div class="pi-field">
                  <label>{{ $t('Birthdate') }}</label>
                  <div class="pi-birth">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Day')"
                      vid="date"
                      rules="required"
                    >
                      <v-select
                        id="date"
                        v-model="user.profile.date"
                        class="pi-select"
                        :clearable="false"
                        :searchable="false"
                        :options="dates"
                        :selectable="option => ! option.includes('Gün')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Month')"
                      vid="month"
                      rules="required"
                    >
                      <v-select
                        id="month"
                        v-model="birthMonth"
                        class="pi-select"
                        :clearable="false"
                        :searchable="false"
                        :options="months"
                        :selectable="option => !option.value.includes('Ay')"
                        label="text"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Year')"
                      vid="year"
                      rules="required"
                    >
                      <v-select
                        id="year"
                        v-model="user.profile.year"
                        class="pi-select"
                        :clearable="false"
                        :searchable="false"
                        :options="years"
                        :selectable="option => ! option.includes('Yıl')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </div>
                </div>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- ============ MOMENTS ============ -->
        <div class="pi-card pi-moments">
          <div
            class="pi-moments-glow"
            aria-hidden="true"
          />
          <div class="pi-section-head">
            <div class="pi-section-title">
              <span class="pi-section-icon pink">
                <feather-icon
                  icon="ImageIcon"
                  size="20"
                />
              </span>
              <div>
                <h3>{{ $t('Moments') }}</h3>
                <p>{{ $t('Moments you share will appear here.') }}</p>
              </div>
            </div>
            <router-link
              :to="{ name: 'moments' }"
              class="pi-round-btn"
            >
              <feather-icon
                icon="ChevronRightIcon"
                size="20"
              />
            </router-link>
          </div>

          <div
            v-if="momentsLoading && moments.length === 0"
            class="text-center py-3"
          >
            <b-spinner variant="primary" />
          </div>
          <div
            v-else-if="moments.length > 0"
            class="pi-moments-grid"
          >
            <div
              v-for="moment in moments"
              :key="moment.id"
              class="pi-moment"
              @click="openMomentModal(moment)"
            >
              <template v-if="isVideo(moment.media_type)">
                <video
                  :src="moment.media_url"
                  muted
                  preload="metadata"
                />
                <span class="pi-moment-play">
                  <font-awesome-icon icon="fa-solid fa-play" />
                </span>
              </template>
              <img
                v-else
                :src="moment.media_url"
                alt=""
              >
              <span class="pi-moment-likes">
                <font-awesome-icon icon="fa-solid fa-heart" />
                {{ moment.like_count || 0 }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="pi-moments-empty"
          >
            <feather-icon
              icon="GiftIcon"
              size="44"
            />
            <span>{{ $t('No moments yet') }}</span>
          </div>
          <div
            v-if="momentsHasMore && moments.length > 0"
            class="text-center mt-2"
          >
            <button
              type="button"
              class="pi-btn pi-btn-outline"
              :disabled="momentsLoading"
              @click="getMoments()"
            >
              <b-spinner
                v-if="momentsLoading"
                small
                class="mr-1"
              />
              {{ $t('Load more') }}
            </button>
          </div>
        </div>
      </template>
    </b-overlay>

    <!-- ============ MOMENT DETAY ============ -->
    <b-modal
      v-model="momentModal"
      centered
      hide-header
      hide-footer
      size="md"
      modal-class="pi-moment-modal"
      @hidden="selectedMoment = null"
    >
      <div v-if="selectedMoment">
        <div class="d-flex justify-content-end mb-1">
          <span
            class="cursor-pointer"
            @click="momentModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </span>
        </div>
        <video
          v-if="isVideo(selectedMoment.media_type)"
          :src="selectedMoment.media_url"
          class="pi-moment-detail-media"
          controls
          autoplay
        />
        <img
          v-else
          :src="selectedMoment.media_url"
          class="pi-moment-detail-media"
          alt=""
        >
        <p
          v-if="selectedMoment.content"
          class="mt-1 mb-1"
        >
          {{ selectedMoment.content }}
        </p>
        <div class="d-flex justify-content-between align-items-center mt-1">
          <span>
            <font-awesome-icon icon="fa-solid fa-heart" />
            {{ selectedMoment.like_count || 0 }}
          </span>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="deleteMoment(selectedMoment)"
          >
            {{ $t('Delete') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ============ MEDYA (GALERI) MODALI — eski profil sayfasından taşındı ============ -->
    <b-overlay
      :show="loading"
      no-center
      variant="transparent"
      rounded="sm"
      style="z-index: 999999"
    >
      <template #overlay>
        <loading-spinner />
      </template>
      <input
        ref="bondingVideoInput"
        type="file"
        accept="video/*"
        style="display: none"
        @change="handleBondingFileSelected"
      >
      <b-modal
        v-model="modalGallery"
        hide-header-close
        hide-header
        hide-footer
        centered
        size="xl"
        modal-class="pi-gallery-modal"
      >
        <div class="pi-gallery-head">
          <div class="pi-gallery-title">
            <span class="pi-gallery-icon">
              <feather-icon
                icon="ImageIcon"
                size="20"
              />
            </span>
            <div>
              <h3>{{ $t('Edit your medias') }}</h3>
              <p>{{ $t('Photos') }} · {{ publicImages.length }}</p>
            </div>
          </div>
          <button
            type="button"
            class="pi-gallery-close"
            :aria-label="$t('Close')"
            @click="modalGallery = false"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </button>
        </div>

        <div
          v-viewer
          class="pi-gallery-grid"
        >
          <!-- Fotograf ekle -->
          <div
            class="pi-gallery-add"
            :class="{ disabled: loading }"
            role="button"
            tabindex="0"
            @click="!loading ? launchFilePicker('inputPublicImage') : null"
            @keyup.enter="!loading ? launchFilePicker('inputPublicImage') : null"
          >
            <input
              ref="inputPublicImage"
              type="file"
              style="display:none"
              accept="image/*"
              @change="addedImage"
            >
            <span class="pi-gallery-add-icon">
              <feather-icon
                icon="PlusIcon"
                size="26"
              />
            </span>
            <span class="pi-gallery-add-text">{{ $t('Add photo') }}</span>
          </div>

          <div
            v-for="image in publicImages"
            :key="image.id"
            class="pi-gallery-item"
          >
            <div class="pi-gallery-photo">
              <img
                :src="image.secureUrl"
                alt=""
              >
              <button
                type="button"
                class="pi-gallery-remove"
                :title="$t('Delete')"
                :disabled="loading"
                @click="removeImage(image)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="15"
                  :data-disabled="true"
                />
              </button>
              <div class="pi-gallery-badges">
                <span
                  v-if="profileImageId === image.id"
                  class="pi-badge pi-badge-profile"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="12"
                  />
                  {{ $t('Profile photo') }}
                </span>
                <span
                  v-if="!image.approved"
                  class="pi-badge pi-badge-pending"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                  />
                  {{ $t('Waiting for approval') }}
                </span>
              </div>
            </div>
            <button
              type="button"
              class="pi-gallery-make"
              :disabled="profileImageId === image.id || !image.approved || loading"
              @click="setUserProfileImage(image)"
            >
              {{ $t('Make profile photo') }}
            </button>
          </div>
        </div>
      </b-modal>

      <b-modal
        v-model="bondingPreviewModal"
        centered
        hide-header-close
        hide-footer
        size="lg"
        :title="$t('Upload video')"
        @hidden="handleBondingPreviewHidden"
      >
        <div
          v-if="bondingPreviewUrl"
          class="bonding-preview-modal"
        >
          <video
            class="bonding-preview-video"
            :src="bondingPreviewUrl"
            controls
            playsinline
          />
          <p
            v-if="bondingDuration"
            class="text-muted mt-1 mb-1"
          >
            {{ $t('Video duration') }}: {{ Math.round(bondingDuration) }} {{ $t('seconds') }}
          </p>
          <p class="bonding-terms">
            {{ $t('Bonding terms text') }}
          </p>
          <b-form-checkbox
            v-model="bondingTermsAccepted"
            class="mb-2"
          >
            {{ $t('I accept the terms') }}
          </b-form-checkbox>
          <b-button
            variant="primary"
            block
            :disabled="!bondingTermsAccepted || bondingUploading"
            @click="publishBondingVideo"
          >
            <b-spinner
              v-if="bondingUploading"
              small
              class="mr-1"
            />
            {{ $t('Publish') }}
          </b-button>
        </div>
      </b-modal>
      <b-modal
        v-model="bondingProgressModal"
        centered
        hide-header-close
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        :title="$t('Uploading video')"
      >
        <div class="bonding-progress-modal">
          <p class="mb-1">
            {{ bondingProcessing ? $t('Preparing video') : $t('Uploading video') }}
          </p>
          <b-progress
            :value="bondingUploadProgress"
            :max="100"
            height="12px"
            animated
          />
          <div class="text-center mt-1">
            {{ bondingUploadProgress }}%
          </div>
        </div>
      </b-modal>
    </b-overlay>

    <!-- ============ E-POSTA / TELEFON / PIN MODALLARI (aynen korundu) ============ -->
    <b-modal
      v-model="modalUpdateEmail"
      centered
      hide-header-close
      hide-footer
      hide-header
      size="md"
    >
      <div
        class="d-flex justify-content-end fs-12 color-gray cursor-pointer"
        @click="modalUpdateEmail = false"
      >
        {{ $t('Close') }}
      </div>
      <b-row>
        <b-col md="12">
          <div class="d-flex justify-content-center align-items-center flex-column pt-3 pl-3 pr-3">
            <h5 class="fs-22 fw-600 text-center color-black">
              {{ $t('Updating email address') }}
            </h5>
          </div>
        </b-col>
        <b-col
          md="12"
          class="p-3"
        >
          <validation-observer
            ref="emailForm"
            #default="{invalid}"
          >
            <b-form
              class="auth-login-form"
              @submit.prevent="emailUpdateVerification"
            >
              <b-form-group label-for="login-phone">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('New email address')"
                  rules="required|email"
                >
                  <b-form-input
                    id="forgot-password-email"
                    v-model="newEmail"
                    :state="errors.length > 0 ? false:null"
                    :placeholder="$t('New email address')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-button
                class="mt-2"
                type="submit"
                variant="primary"
                block
                :disabled="loading || invalid"
              >
                <b-spinner
                  v-if="loading"
                  small
                />
                {{ $t('Continue') }}
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      v-model="modalUpdatePhone"
      centered
      hide-header-close
      hide-footer
      hide-header
      size="md"
    >
      <div
        class="d-flex justify-content-end fs-12 color-gray cursor-pointer"
        @click="modalUpdatePhone = false"
      >
        {{ $t('Close') }}
      </div>
      <b-row>
        <b-col md="12">
          <div class="d-flex justify-content-center align-items-center flex-column pt-3 pl-3 pr-3">
            <h5 class="fs-22 fw-600 text-center color-black">
              {{ $t('Updating phone number') }}
            </h5>
          </div>
        </b-col>
        <b-col
          md="12"
          class="p-3"
        >
          <validation-observer
            ref="phoneForm"
            #default="{invalid}"
          >
            <b-form
              class="auth-login-form"
              @submit.prevent="phoneNumberVerification"
            >
              <b-form-group label-for="login-phone">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Mobile phone number')"
                  vid="phone"
                  rules="required"
                >
                  <vue-tel-input
                    v-model="newPhone"
                    mode="auto"
                    :input-options="{placeholder: $t('Mobile phone number')}"
                    valid-characters-only
                    :style-classes="['phone-input']"
                    @open="onDropdownOpen(true)"
                    @close="onDropdownOpen(false)"
                    @country-changed="countryChanged"
                    @validate="phoneValidate"
                  >
                    <template v-slot:arrow-icon>
                      <span class="font-small-3 mr-05">
                        {{ country ? '+' + country.dialCode : '' }}
                      </span>
                      <font-awesome-icon
                        v-if="open"
                        icon="fa-solid fa-chevron-up"
                      />
                      <font-awesome-icon
                        v-else
                        icon="fa-solid fa-chevron-down"
                      />
                    </template>
                  </vue-tel-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-button
                class="mt-2"
                type="submit"
                variant="primary"
                block
                :disabled="loading || invalid"
              >
                <b-spinner
                  v-if="loading"
                  small
                />
                {{ $t('Continue') }}
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      v-model="modalPinCode"
      centered
      hide-header-close
      hide-footer
      hide-header
      size="md"
    >
      <div
        class="d-flex justify-content-end fs-12 color-gray cursor-pointer"
        @click="modalPinCode = false"
      >
        {{ $t('Close') }}
      </div>
      <b-row class="password-information">
        <b-col md="12">
          <div class="d-flex justify-content-center align-items-center flex-column pt-3 pl-3 pr-3">
            <h5 class="fs-22 fw-600 text-center color-black mb-1">
              {{ $t('PIN Code') }}
            </h5>
          </div>
        </b-col>
        <b-col
          sm="12"
          class="mt-2"
        >
          <div class="d-flex justify-content-center">
            <validation-provider
              #default="{ errors }"
              :name="$t('PIN Code')"
              vid="password"
            >
              <PincodeInput v-model="pinCode" />
              <br>
              <div class="text-danger text-center mt-2">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </b-col>
        <b-col sm="12">
          <p class="send-again">
            {{ $t('Code not received?') }}
            <span
              class="link"
              @click="sendAgainPinCode"
            >
              {{ $t('Send again') }}
            </span>
          </p>
        </b-col>
        <b-col md="12">
          <b-button
            class="mt-2 mb-1"
            type="submit"
            variant="primary"
            block
            :disabled="loading"
            @click="updateEmailOrPhone"
          >
            <b-spinner
              v-if="loading"
              small
            />
            {{ $t('Approve') }}
          </b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
// eslint-disable-next-line no-unused-vars
import { required, email } from '@validations'
import {
  BButton, BCol, BForm, BFormCheckbox, BFormGroup, BFormInput, BFormTextarea, BImg, BModal, BOverlay, BProgress,
  BRow, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import LoadingSpinner from '@/components/LoadingSpinner'
import {
  gtmTrackNewEvent, showNotify, snapshotFile, throwDefaultError,
} from '@/helpers/helpers'
import vSelect from 'vue-select'
import PincodeInput from 'vue-pincode-input'

const iconClose = require('@/assets/images/icons/close-icon.svg')
const iconCloseRed = require('@/assets/images/icons/close-icon-red.svg')

export default {
  components: {
    BButton,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BImg,
    BModal,
    BOverlay,
    BProgress,
    BRow,
    BSpinner,

    LoadingSpinner,

    PincodeInput,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      // Profil formu
      aboutDraft: '',
      birthMonth: null,
      country: null,
      currentUsername: null,
      dates: [
        'Gün', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
        '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31',
      ],
      editingAbout: false,
      months: [
        { text: this.$t('Month'), value: this.$t('Month') },
        { text: this.$t('January'), value: '1' },
        { text: this.$t('February'), value: '2' },
        { text: this.$t('March'), value: '3' },
        { text: this.$t('April'), value: '4' },
        { text: this.$t('May'), value: '5' },
        { text: this.$t('June'), value: '6' },
        { text: this.$t('July'), value: '7' },
        { text: this.$t('August'), value: '8' },
        { text: this.$t('September'), value: '9' },
        { text: this.$t('October'), value: '10' },
        { text: this.$t('November'), value: '11' },
        { text: this.$t('December'), value: '12' },
      ],
      loading: false,
      modalPinCode: false,
      modalUpdateEmail: false,
      modalUpdatePhone: false,
      newEmail: '',
      newPhone: '',
      open: false,
      pinCode: '',
      updateType: 'email',
      user: null,
      valid: false,

      // Seviyeler
      levels: [
        { key: 'member', label: 'BOHO MEMBER', icon: require('@/assets/images/levels/member.png') },
        { key: 'bronze', label: 'BRONZE', icon: require('@/assets/images/levels/bronze.png') },
        { key: 'silver', label: 'SILVER', icon: require('@/assets/images/levels/silver.png') },
        { key: 'gold', label: 'GOLD', icon: require('@/assets/images/levels/gold.png') },
      ],

      // Moments
      moments: [],
      momentsHasMore: true,
      momentsLimit: 12,
      momentsLoading: false,
      momentsOffset: 0,
      momentModal: false,
      selectedMoment: null,

      // Medya / galeri (eski profil sayfasından)
      confirmModalOptions: {
        title: 'Onay',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Sil',
        cancelTitle: 'Vazgeç',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      },
      iconClose,
      iconCloseRed,
      medias: [],
      modalGallery: false,

      // Bonding (eski profil sayfasından)
      bondingDuration: 0,
      bondingFile: null,
      bondingPreviewModal: false,
      bondingPreviewUrl: '',
      bondingProcessing: false,
      bondingProgressModal: false,
      bondingTermsAccepted: false,
      bondingUploadProgress: 0,
      bondingUploading: false,
    }
  },
  computed: {
    canUploadBonding() {
      const currentUser = this.$store.getters['auth/user']
      const country = currentUser?.profile?.country || currentUser?.country || ''
      const normalizedCountry = country.toString().toUpperCase()

      return currentUser?.freeCallUser === true && !['TR', 'AZ'].includes(normalizedCountry)
    },
    phoneWithCountryCode() {
      return this.country
        ? `+${this.country.dialCode}${this.newPhone.replace(/ /g, '')}`
        : this.newPhone.replace(/ /g, '')
    },
    profileImageId() {
      return this.$store.getters['auth/profileImageId']
    },
    publicImages() {
      return this.medias.filter(media => media.type === 1 && !media.hidden)
    },
    userAge() {
      if (!this.user || !this.user.profile || !this.user.profile.year) return null
      const year = parseInt(this.user.profile.year, 10)
      return Number.isNaN(year) ? null : new Date().getFullYear() - year
    },
    years() {
      const _years = ['Yıl']
      const lastYear = new Date().getFullYear() - 18

      for (let i = 0; i < 100; i++) {
        _years.push((lastYear - i).toString())
      }

      return _years
    },
  },
  watch: {
    modalGallery(val) {
      if (val) {
        this.getMedias()
      }
    },
  },
  created() {
    this.getProfile(true)
  },
  beforeDestroy() {
    this.cleanupBondingPreview()
  },
  methods: {
    // ---------- Profil ----------
    getProfile(loadMoments = false) {
      this.loading = true
      return useJwt.getMe()
        .then(response => {
          const data = response.data.body
          this.birthMonth = this.months.find(m => String(data.profile.month) === m.value) || null
          this.currentUsername = data.userName
          this.user = data
          this.$store.dispatch('auth/updateUser', data)

          if (loadMoments) {
            this.getMoments(true)
          }
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    update() {
      if (!this.$refs.form) return

      this.$refs.form.validate().then(success => {
        if (success) {
          this.saveProfile()
        }
      })
    },
    saveProfile() {
      const data = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        deviceId: this.$store.getters['app/deviceId'],
      }
      const profile = {
        description: this.user.profile.description,
        date: this.user.profile.date,
        month: this.birthMonth ? this.birthMonth.value : this.user.profile.month,
        year: this.user.profile.year,
        deviceId: this.$store.getters['app/deviceId'],
      }

      this.loading = true

      return Promise.all([useJwt.updateMe(data), useJwt.updateProfile(profile)])
        .then(() => {
          showNotify(this.$toast, this.$t('Update successful'), 'success')
          this.getProfile()

          gtmTrackNewEvent('saveuser', {
            category: 'Hesap',
            action: 'SaveUser',
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    startEditAbout() {
      this.aboutDraft = this.user.profile.description || ''
      this.editingAbout = true
    },
    cancelEditAbout() {
      this.editingAbout = false
      this.aboutDraft = ''
    },
    saveAbout() {
      this.user.profile.description = this.aboutDraft
      this.editingAbout = false
      this.saveProfile()
    },
    copyIdentifier() {
      const text = String(this.user.identifier)
      const done = () => showNotify(this.$toast, this.$t('Copied'), 'success')

      if (this.$copyText) {
        this.$copyText(text).then(done).catch(() => {})
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(done).catch(() => {})
      }
    },
    checkBadge(level) {
      if (!this.user) return true

      const userBadgeLevel = this.user.profile?.userBadgeLevel ? this.user.profile.userBadgeLevel.toLowerCase() : ''

      if (level === 'member') {
        if (['boho_member', 'bronze_member', 'silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'bronze') {
        if (['bronze_member', 'silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'silver') {
        if (['silver_member', 'gold_member'].includes(userBadgeLevel)) return false
      } else if (level === 'gold') {
        if (['gold_member'].includes(userBadgeLevel)) return false
      }

      return true
    },

    // ---------- E-posta / telefon ----------
    countryChanged(country) {
      this.country = country
    },
    emailUpdate() {
      this.loading = true

      useJwt.emailUpdate({
        email: this.newEmail,
        pinCode: this.pinCode,
        deviceId: this.$store.getters['app/deviceId'],
      })
        .then(() => {
          showNotify(this.$toast, this.$t('Your e-mail address has been updated'), 'success')
          this.updateType = ''
          this.newEmail = ''
          this.pinCode = ''
          this.modalPinCode = false
          this.modalUpdateEmail = false

          this.getProfile()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    emailUpdateVerification() {
      this.$refs.emailForm.validate().then(success => {
        if (success) {
          this.loading = true

          useJwt.emailUpdateVerification({ email: this.newEmail })
            .then(response => {
              showNotify(this.$toast, response.data.body, 'success')
              this.updateType = 'email'
              this.pinCode = ''
              this.modalPinCode = true
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
    },
    onDropdownOpen(open) {
      this.open = open
    },
    phoneNumberUpdate() {
      this.loading = true

      useJwt.phoneNumberUpdate({
        phoneNumber: this.phoneWithCountryCode,
        pinCode: this.pinCode,
        deviceId: this.$store.getters['app/deviceId'],
      })
        .then(() => {
          showNotify(this.$toast, this.$t('Your phone number has been updated'), 'success')
          this.updateType = ''
          this.newPhone = ''
          this.pinCode = ''
          this.modalPinCode = false
          this.modalUpdatePhone = false

          this.getProfile()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    phoneValidate(status) {
      this.valid = status ? status.valid : false
      if (this.$refs.phoneForm) this.$refs.phoneForm.reset()
    },
    phoneNumberVerification() {
      if (!this.valid) {
        this.$refs.phoneForm.setErrors({
          phone: this.$t('Check phone number'),
        })
        return false
      }
      this.$refs.phoneForm.validate().then(success => {
        if (success) {
          this.loading = true

          useJwt.phoneNumberVerification({ phoneNumber: this.phoneWithCountryCode })
            .then(response => {
              showNotify(this.$toast, response.data.body, 'success')
              this.updateType = 'phone'
              this.pinCode = ''
              this.modalPinCode = true
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => {
              this.loading = false
            })
        }
      })
      return true
    },
    sendAgainPinCode() {
      if (this.updateType === 'email') {
        this.emailUpdateVerification()
      } else {
        this.phoneNumberVerification()
      }
    },
    updateEmailOrPhone() {
      if (this.updateType === 'email') {
        this.emailUpdate()
      } else {
        this.phoneNumberUpdate()
      }
    },

    // ---------- Moments ----------
    async getMoments(reset = false) {
      if (this.momentsLoading) return
      if (!this.user?.id) return

      if (reset) {
        this.moments = []
        this.momentsOffset = 0
        this.momentsHasMore = true
      }

      if (!this.momentsHasMore) return

      this.momentsLoading = true
      try {
        const response = await useJwt.getUserPosts(this.user.id, this.momentsOffset, this.momentsLimit)
        const newMoments = response.data?.body || []

        if (newMoments.length < this.momentsLimit) {
          this.momentsHasMore = false
        }

        this.moments = [...this.moments, ...newMoments]
        this.momentsOffset += this.momentsLimit
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching moments:', error)
      } finally {
        this.momentsLoading = false
      }
    },
    isVideo(mediaType) {
      return mediaType?.toLowerCase()?.includes('video')
    },
    openMomentModal(moment) {
      this.selectedMoment = moment
      this.momentModal = true
    },
    deleteMoment(moment) {
      if (!moment) return

      this.$bvModal.msgBoxConfirm(this.$t('Are you sure you want to delete?'), {
        ...this.confirmModalOptions,
        title: this.$t('Confirmation'),
        okTitle: this.$t('Delete'),
        cancelTitle: this.$t('Cancel'),
      }).then(value => {
        if (!value) return

        this.loading = true
        useJwt.deletePost(moment.id)
          .then(() => {
            this.moments = this.moments.filter(m => m.id !== moment.id)
            showNotify(this.$toast, this.$t('Deleted successfully'), 'success')
            this.momentModal = false
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
          .finally(() => {
            this.loading = false
          })
      })
    },

    // ---------- Medya / galeri ----------
    async addedImage(event) {
      const input = event.target
      const picked = input.files && input.files[0]
      if (!picked) return

      const file = await snapshotFile(picked)

      const formData = new FormData()
      formData.append('file', file, file.name || 'upload')

      this.loading = true
      useJwt.uploadImage(formData)
        .then(() => {
          this.getMedias()
          this.getProfile()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
          this.loading = false
        })
        .finally(() => {
          input.value = ''
        })
    },
    getMedias() {
      this.loading = true

      useJwt.getMedias(this.$store.getters['auth/userId'])
        .then(response => {
          this.medias = Array.isArray(response.data.body) ? response.data.body : []
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    launchFilePicker(name) {
      this.$refs[name].click()
    },
    removeImage(image) {
      this.$bvModal.msgBoxConfirm(this.$t('Are you sure you want to delete?'), this.confirmModalOptions)
        .then(value => {
          if (value) {
            this.loading = true

            useJwt.removeImage(image.id)
              .then(() => {
                this.getProfile()
                this.getMedias()
                showNotify(this.$toast, this.$t('Photo deleted successfully'), 'success')
              })
              .catch(error => {
                throwDefaultError(this.$toast, error)
              })
              .finally(() => { this.loading = false })
          }
        })
    },
    setUserProfileImage(image) {
      if (this.profileImageId !== image.id && image.approved) {
        this.$bvModal.msgBoxConfirm(this.$t('Are you sure you want to make a profile picture?'), {
          ...this.confirmModalOptions,
          okVariant: 'success',
          okTitle: this.$t('Make profile photo'),
        })
          .then(value => {
            if (value) {
              this.loading = true

              useJwt.setUserProfileImage(image.id)
                .then(() => {
                  this.getProfile()
                  showNotify(this.$toast, `${this.$t('Profile photo changed successfully')}.`, 'success')
                })
                .catch(error => {
                  throwDefaultError(this.$toast, error)
                })
                .finally(() => { this.loading = false })
            }
          })
      }
    },

    // ---------- Bonding ----------
    cleanupBondingPreview() {
      if (this.bondingPreviewUrl) {
        URL.revokeObjectURL(this.bondingPreviewUrl)
      }
      this.bondingDuration = 0
      this.bondingFile = null
      this.bondingPreviewUrl = ''
      this.bondingTermsAccepted = false
    },
    getVideoDuration(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const video = document.createElement('video')

        video.preload = 'metadata'
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url)
          resolve(video.duration)
        }
        video.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error(this.$t('Video duration could not be read')))
        }
        video.src = url
      })
    },
    async handleBondingFileSelected(event) {
      const input = event.target
      const picked = input.files && input.files[0]

      if (!picked) {
        input.value = ''
        return
      }

      if (!picked.type || !picked.type.startsWith('video/')) {
        showNotify(this.$toast, this.$t('Please select a video file'), 'danger')
        input.value = ''
        return
      }

      const file = await snapshotFile(picked)
      input.value = ''

      this.cleanupBondingPreview()
      this.bondingFile = file
      this.bondingPreviewUrl = URL.createObjectURL(file)
      this.bondingPreviewModal = true

      this.getVideoDuration(file)
        .then(duration => {
          this.bondingDuration = duration
        })
        .catch(error => {
          showNotify(this.$toast, error.message || this.$t('Video duration could not be read'), 'danger')
          this.bondingPreviewModal = false
          this.cleanupBondingPreview()
        })
    },
    handleBondingPreviewHidden() {
      if (!this.bondingUploading) {
        this.cleanupBondingPreview()
      }
    },
    openBondingFilePicker() {
      this.$nextTick(() => {
        if (this.$refs.bondingVideoInput) this.$refs.bondingVideoInput.click()
      })
    },
    async publishBondingVideo() {
      if (!this.bondingFile) {
        showNotify(this.$toast, this.$t('Please select a video file'), 'danger')
        return
      }

      try {
        this.bondingProcessing = true
        this.bondingUploading = true
        this.bondingUploadProgress = 0
        this.bondingProgressModal = true

        const duration = this.bondingDuration || await this.getVideoDuration(this.bondingFile)
        this.bondingDuration = duration

        if (duration < 20) {
          throw new Error(this.$t('Bonding videos must be at least 20 seconds'))
        }

        if (duration > 22) {
          showNotify(this.$toast, this.$t('Videos longer than 22 seconds will use the first 22 seconds'), 'warning')
        }

        this.bondingProcessing = false
        const response = await useJwt.uploadBondingVideo(this.bondingFile, progress => {
          this.bondingUploadProgress = progress
        })
        const message = response?.data?.body || this.$t('Bonding video uploaded successfully')

        this.bondingUploadProgress = 100
        this.bondingPreviewModal = false
        showNotify(this.$toast, message, 'success')
        this.cleanupBondingPreview()
        this.getProfile()
      } catch (error) {
        showNotify(this.$toast, error.message || this.$t('Upload failed'), 'danger')
      } finally {
        this.bondingProcessing = false
        this.bondingUploading = false
        this.bondingProgressModal = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$pi-text: #f4f1ff;
$pi-muted: rgba(226, 220, 255, 0.68);
$pi-card: rgba(32, 22, 72, 0.72);
$pi-border: rgba(150, 110, 255, 0.28);
$pi-input: rgba(14, 10, 40, 0.55);
$pi-purple: #8b5cf6;
$pi-purple-2: #a855f7;
$pi-pink: #ea03b2;

.profile-information-page {
  color: $pi-text;
  margin: 0 -25px;
}

.pi-card {
  position: relative;
  background: $pi-card;
  border: 1px solid $pi-border;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(6, 2, 24, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
  margin-bottom: 20px;
}

/* ---------------- HERO ---------------- */
.pi-hero {
  background: linear-gradient(135deg, #2b1a63 0%, #1d1247 45%, #3a1356 100%);
}

.pi-hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.pi-hero-glow {
  position: absolute;
  top: -40%;
  right: -10%;
  width: 70%;
  height: 140%;
  background: radial-gradient(ellipse at 70% 40%, rgba(234, 3, 178, 0.35) 0%, rgba(147, 51, 234, 0.18) 40%, transparent 70%);
  filter: blur(10px);
}

.pi-hero-heart {
  position: absolute;

  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  &.heart-big {
    right: 6%;
    top: -26px;
    width: 150px;
    height: 150px;
    transform: rotate(14deg);
    filter: drop-shadow(0 0 18px rgba(234, 3, 178, 0.55));
    animation: piHeartPulse 6s ease-in-out infinite;
  }

  &.heart-mid {
    right: 24%;
    top: 30px;
    width: 70px;
    height: 70px;
    opacity: 0.55;
    transform: rotate(-12deg);
    filter: drop-shadow(0 0 12px rgba(234, 3, 178, 0.45));
    animation: piHeartPulse 7.5s ease-in-out infinite reverse;
  }

  &.heart-small {
    width: 22px;
    height: 22px;
    filter: drop-shadow(0 0 8px rgba(234, 3, 178, 0.85));
    animation: piHeartFloat 4.8s ease-in-out infinite;
  }

  &.heart-small-1 { right: 3%; top: 140px; }
  &.heart-small-2 { right: 36%; top: 14px; width: 16px; height: 16px; animation-delay: -1.6s; }
  &.heart-small-3 { right: 16%; top: 128px; width: 14px; height: 14px; animation-delay: -3s; }
}

@keyframes piHeartPulse {
  0%, 100% { opacity: 0.75; transform: translateY(0) rotate(14deg) scale(1); }
  50% { opacity: 1; transform: translateY(-6px) rotate(10deg) scale(1.06); }
}

@keyframes piHeartFloat {
  0%, 100% { transform: translateY(0); opacity: 0.8; }
  50% { transform: translateY(-8px); opacity: 1; }
}

.pi-hero-content {
  position: relative;
  z-index: 1;
  padding: 24px 28px;
}

.pi-hero-top {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.pi-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.pi-avatar {
  width: 164px;
  height: 164px;
  border-radius: 50%;
  padding: 5px;
  background: linear-gradient(140deg, #ff5fb7, #a855f7 55%, #ff9f6b);
  box-shadow: 0 0 30px rgba(234, 3, 178, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #1d1247;
  }
}

.pi-avatar-edit {
  position: absolute;
  right: 6px;
  bottom: 8px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #1d1247;
  background: linear-gradient(135deg, $pi-purple, $pi-purple-2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform .15s ease;

  &:hover { transform: scale(1.08); }
}

.pi-identity {
  flex: 1 1 220px;
  min-width: 0;
}

.pi-name {
  color: #fff !important;
  font-size: 30px;
  line-height: 36px;
  font-weight: 700;
  margin-bottom: 8px;
  word-break: break-word;

  .pi-age {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
  }
}

.pi-id,
.pi-location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $pi-muted;
  font-size: 16px;
  margin-bottom: 6px;
}

.pi-location svg {
  color: #ff4fa8;
}

.pi-icon-btn {
  background: transparent;
  border: 0;
  color: $pi-muted;
  padding: 2px;
  cursor: pointer;
  display: inline-flex;
  // Simge 20px; görünüm aynı, dokunma alanı görünmez çerçeveyle ~44px (telefonda parmakla basılabilsin)
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -12px;
    right: -12px;
    bottom: -12px;
    left: -12px;
  }

  &:hover { color: #fff; }
}

.pi-about {
  flex: 1 1 320px;
  max-width: 560px;
  margin-left: auto;
  background: rgba(20, 12, 54, 0.55);
  border: 1px solid rgba(170, 140, 255, 0.35);
  border-radius: 16px;
  padding: 16px 20px;
  backdrop-filter: blur(6px);

  p {
    color: $pi-muted;
    margin: 0;
    font-size: 14px;
    white-space: pre-line;
  }
}

.pi-about-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  margin-bottom: 8px;
}

.pi-about-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.pi-levels {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin-top: 22px;
  padding: 16px 22px;
  background: rgba(20, 12, 54, 0.5);
  border: 1px solid rgba(150, 110, 255, 0.22);
  border-radius: 16px;
}

.pi-level {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.passive {
    .pi-level-icon img { filter: grayscale(1); opacity: .55; }
    .pi-level-label { color: $pi-muted; }
    .pi-level-bar span { width: 0; }
  }
}

.pi-level-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pi-level-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 26px;
    height: 26px;
    object-fit: contain;
  }
}

.pi-level-label {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  letter-spacing: .02em;
}

.pi-level-bar {
  height: 6px;
  border-radius: 6px;
  background: rgba(139, 92, 246, 0.25);
  overflow: hidden;

  span {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    background: linear-gradient(90deg, $pi-purple, $pi-pink);
    transition: width .4s ease;
  }
}

.pi-bonding {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: $pi-muted;
  font-size: 13px;
}

/* ---------------- BUTTONS ---------------- */
.pi-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 12px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid transparent;
  transition: filter .15s ease, background .15s ease;

  &:disabled { opacity: .6; cursor: not-allowed; }
}

.pi-btn-primary {
  color: #fff;
  background: linear-gradient(135deg, $pi-purple, $pi-purple-2);
  box-shadow: 0 6px 18px rgba(139, 92, 246, 0.35);

  &:hover:not(:disabled) { filter: brightness(1.1); }
}

.pi-btn-outline {
  color: #fff;
  background: rgba(14, 10, 40, 0.5);
  border-color: rgba(170, 140, 255, 0.35);

  &:hover:not(:disabled) { background: rgba(139, 92, 246, 0.2); }
}

.pi-btn-ghost {
  color: $pi-muted;
  background: transparent;
  border-color: rgba(170, 140, 255, 0.25);
}

.pi-save {
  border-radius: 999px;
  padding: 10px 22px;
}

.pi-round-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(14, 10, 40, 0.6);
  border: 1px solid rgba(170, 140, 255, 0.35);

  &:hover { background: rgba(139, 92, 246, 0.25); color: #fff; }
}

/* ---------------- FORM ---------------- */
.pi-form-card,
.pi-moments {
  padding: 22px 28px 26px;
}

.pi-section-head {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.pi-section-title {
  display: flex;
  align-items: center;
  gap: 14px;

  h3 {
    color: #fff !important;
    font-size: 19px;
    font-weight: 700;
    margin: 0 0 2px;
  }

  p {
    color: $pi-muted;
    font-size: 13px;
    margin: 0;
  }
}

.pi-section-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, $pi-purple, $pi-purple-2);
  flex-shrink: 0;

  &.pink { background: linear-gradient(135deg, #ff4fa8, #c026d3); }
}

.pi-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.pi-field {
  min-width: 0;

  label {
    display: block;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
  }
}

.pi-split {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.pi-input-row {
  display: flex;
  gap: 10px;

  .pi-input { flex: 1; }
}

.pi-input-with-icon {
  position: relative;

  svg {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $pi-muted;
  }
}

.pi-input,
.pi-textarea {
  height: 44px;
  background: $pi-input !important;
  border: 1px solid rgba(170, 140, 255, 0.28) !important;
  border-radius: 12px !important;
  color: #fff !important;
  padding: 0 14px;
  box-shadow: none !important;

  &:focus {
    border-color: $pi-purple-2 !important;
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.2) !important;
  }

  &:disabled,
  &[readonly] {
    opacity: 1;
    color: rgba(255, 255, 255, 0.85) !important;
    -webkit-text-fill-color: rgba(255, 255, 255, 0.85);
  }
}

.pi-textarea {
  height: auto;
  padding: 10px 12px;
}

.pi-gender {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.pi-gender-option {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  border-radius: 999px;
  background: $pi-input;
  border: 1px solid rgba(170, 140, 255, 0.22);
  color: $pi-muted;
  font-weight: 500;
  cursor: default;

  .pi-gender-symbol { font-size: 18px; }

  &.active {
    color: #fff;
    border: 2px solid $pi-purple-2;
    background: rgba(139, 92, 246, 0.22);
    box-shadow: 0 0 18px rgba(168, 85, 247, 0.35);
  }
}

.pi-birth {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  gap: 12px;
}

.pi-select {
  ::v-deep .vs__dropdown-toggle {
    height: 44px;
    background: $pi-input;
    border: 1px solid rgba(170, 140, 255, 0.28);
    border-radius: 12px;
    padding: 0 6px;
  }

  ::v-deep .vs__selected,
  ::v-deep .vs__search {
    color: #fff;
  }

  ::v-deep .vs__open-indicator {
    fill: $pi-muted;
  }

  ::v-deep .vs__dropdown-menu {
    background: #1e1648;
    border: 1px solid rgba(170, 140, 255, 0.35);
    border-radius: 12px;
    color: #fff;
  }

  ::v-deep .vs__dropdown-option {
    color: #fff;
  }

  ::v-deep .vs__dropdown-option--highlight {
    background: rgba(139, 92, 246, 0.45);
  }

  ::v-deep .vs__dropdown-option--disabled {
    color: $pi-muted;
    background: transparent;
  }
}

/* ---------------- MOMENTS ---------------- */
.pi-moments {
  min-height: 200px;
}

.pi-moments-glow {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 70%;
  background: radial-gradient(ellipse 60% 80% at 50% 110%, rgba(234, 3, 178, 0.28), transparent 70%);
  pointer-events: none;
}

.pi-moments-empty {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px 0 6px;
  color: #fff;
  font-size: 16px;

  svg {
    color: #ff6bb8;
    filter: drop-shadow(0 0 10px rgba(234, 3, 178, 0.6));
  }
}

.pi-moments-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
}

.pi-moment {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  background: #0d0828;
  border: 1px solid rgba(170, 140, 255, 0.22);

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform .25s ease;
  }

  &:hover img,
  &:hover video { transform: scale(1.04); }
}

.pi-moment-play {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pi-moment-likes {
  position: absolute;
  left: 8px;
  bottom: 8px;
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 999px;
  padding: 2px 8px;

  svg { color: #ff4fa8; }
}

.pi-moment-detail-media {
  width: 100%;
  max-height: 65vh;
  object-fit: contain;
  border-radius: 12px;
  background: #000;
}

/* ---------------- MODALLAR (eski stiller) ---------------- */
.bonding-preview-video {
  width: 100%;
  max-height: 60vh;
  border-radius: 8px;
  background: #000;
}

.bonding-terms {
  color: #5e5873;
  font-size: 14px;
  line-height: 1.45;
}

.bonding-progress-modal {
  text-align: center;
}

.password-information {
  font-family: 'Lexend', sans-serif;

  .send-again {
    margin-top: 14px;
    font-weight: 300;
    font-size: 16px;
    line-height: 25px;
    text-align: center;
    color: #8893A4;

    .link {
      cursor: pointer;
      color: #F9447A;
    }
  }
}

/* ---------------- RESPONSIVE ---------------- */
@media (max-width: 1199px) {
  .pi-about {
    max-width: none;
    margin-left: 0;
    flex-basis: 100%;
  }
}

@media (max-width: 991px) {
  .pi-grid { grid-template-columns: 1fr; }
  .pi-levels { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 575px) {
  .pi-hero-content,
  .pi-form-card,
  .pi-moments { padding: 18px 16px; }

  .pi-hero-top {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }

  /* dikey dizilimde flex-basis yüksekliğe dönüşmesin */
  .pi-identity,
  .pi-about {
    flex: 0 0 auto;
    width: 100%;
    max-width: none;
    margin-left: 0;
  }

  .pi-about { text-align: left; }

  .pi-levels {
    padding: 14px;
    gap: 14px;
  }

  .pi-split { grid-template-columns: 1fr; }

  .pi-input-row .pi-btn { padding: 10px 14px; }

  .pi-save { width: 100%; }

  .pi-hero-heart.heart-big { width: 100px; height: 100px; right: -10px; }
  .pi-hero-heart.heart-mid { display: none; }

  .pi-id,
  .pi-location { justify-content: center; }

  .pi-avatar {
    width: 128px;
    height: 128px;
  }

  .pi-name { font-size: 24px; line-height: 30px; }

  .pi-section-head {
    flex-wrap: wrap;
  }

  .pi-birth { grid-template-columns: 1fr; }
}
</style>

<style lang="scss">
/* Medya (galeri) modalı body'ye taşındığı için global ama .pi-gallery-modal ile sınırlı */
.pi-gallery-modal {
  .modal-content {
    background: linear-gradient(160deg, #24175a 0%, #170f3d 100%);
    border: 1px solid rgba(150, 110, 255, 0.3);
    border-radius: 22px;
    box-shadow: 0 24px 60px rgba(4, 1, 20, 0.6);
    color: #f4f1ff;
  }

  .modal-body {
    padding: 24px 28px 28px;
  }

  .pi-gallery-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 22px;
  }

  .pi-gallery-title {
    display: flex;
    align-items: center;
    gap: 14px;

    h3 {
      color: #fff;
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 2px;
    }

    p {
      color: rgba(226, 220, 255, 0.7);
      font-size: 13px;
      margin: 0;
    }
  }

  .pi-gallery-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #ff4fa8, #c026d3);
  }

  .pi-gallery-close {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid rgba(170, 140, 255, 0.35);
    background: rgba(14, 10, 40, 0.6);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover { background: rgba(139, 92, 246, 0.3); }
  }

  .pi-gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 18px;
  }

  .pi-gallery-add {
    min-height: 260px;
    border-radius: 18px;
    border: 2px dashed rgba(190, 160, 255, 0.5);
    background: rgba(139, 92, 246, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    cursor: pointer;
    transition: background .15s ease, border-color .15s ease;

    &:hover {
      background: rgba(139, 92, 246, 0.2);
      border-color: #c084fc;
    }

    &.disabled {
      opacity: .5;
      cursor: not-allowed;
    }
  }

  .pi-gallery-add-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #8b5cf6, #ea03b2);
    box-shadow: 0 0 22px rgba(234, 3, 178, 0.45);
  }

  .pi-gallery-add-text {
    color: #fff;
    font-weight: 600;
    font-size: 15px;
  }

  .pi-gallery-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .pi-gallery-photo {
    position: relative;
    aspect-ratio: 3 / 4;
    border-radius: 18px;
    overflow: hidden;
    background: #0d0828;
    border: 1px solid rgba(170, 140, 255, 0.25);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: zoom-in;
    }
  }

  .pi-gallery-remove {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 0;
    background: rgba(10, 6, 30, 0.75);
    color: #ff6b8a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(4px);

    &:hover:not(:disabled) { background: #e11d48; color: #fff; }
    &:disabled { opacity: .5; cursor: not-allowed; }
  }

  .pi-gallery-badges {
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .pi-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }

  .pi-badge-profile {
    color: #fff;
    background: linear-gradient(135deg, #8b5cf6, #a855f7);
  }

  .pi-badge-pending {
    color: #1f1300;
    background: #fbbf24;
  }

  .pi-gallery-make {
    width: 100%;
    border: 1px solid rgba(170, 140, 255, 0.4);
    background: rgba(139, 92, 246, 0.18);
    color: #fff;
    border-radius: 12px;
    padding: 9px 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) { background: linear-gradient(135deg, #8b5cf6, #a855f7); }

    &:disabled {
      opacity: .45;
      cursor: not-allowed;
    }
  }

  @media (max-width: 575px) {
    .modal-body { padding: 18px 16px 20px; }
    .pi-gallery-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .pi-gallery-add { min-height: 200px; }
  }
}
</style>
